import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceFaqs } from "@/data/serviceFaqs";
import img from "@/assets/denver-new-construction-prewire.jpg.asset.json";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import img800 from "@/assets/prewire-800.webp.asset.json";


export const Route = createFileRoute("/services/prewire")({
  head: () => ({
    meta: [
      { title: "New Construction Prewire | Apex AV Denver" },
      {
        name: "description",
        content:
          "New construction low-voltage prewire across Denver. Structured cabling for AV, networking, security, lighting, and smart home — properly planned during framing for a future-proof home.",
      },
      { name: "keywords", content: "new construction prewire Denver, low voltage prewire Denver, structured cabling Denver, smart home prewire Denver, home theater prewire Denver, speaker prewire Denver, security camera prewire Denver, network wiring Denver, Cat6 prewire Denver, builder AV prewire Denver, luxury home wiring Denver" },
      { property: "og:title", content: "New Construction Prewire — Apex AV Denver" },
      {
        property: "og:description",
        content: "Low-voltage prewire for luxury new builds across the Denver Metro Area.",
      },
      { property: "og:url", content: "https://apexav.com/services/prewire" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: abs(img.url) },
      { name: "twitter:image", content: abs(img.url) },
      { name: "twitter:title", content: "New Construction Prewire — Apex AV Denver" },
      { name: "twitter:description", content: "Low-voltage prewire for luxury new builds across the Denver Metro Area." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/prewire" }, { rel: "preload", as: "image", href: img800.url, fetchPriority: "high" } as never],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "prewire",
            name: "New Construction Prewire",
            description: "Low-voltage prewire during framing for AV, networking, and smart home in Denver.",
            pageTitle: "New Construction Prewire | Apex AV Denver",
            pageDescription: "New construction low-voltage prewire across Denver. Structured cabling for AV, networking, security, lighting, and smart home — properly planned during framing for a future-proof home.",
            image: img.url,
            faqs: serviceFaqs["prewire"],
            serviceType: "New Construction Prewire",
            offerCatalog: [
              "On-site walkthroughs with your builder, architect, and electrician",
              "Structured cabling: Cat6/6A, fiber, speaker wire, coax, control runs",
              "Home theater, media room, and whole-home audio rough-in",
              "Camera, access control, and security device pre-runs",
              "Lighting control, motorized shade, and HVAC sensor wiring",
              "Conduit pulls and back boxes for future-proof flexibility",
              "Detailed labeling, documentation, and as-built drawings",
            ],
            breadcrumbName: "New Construction Prewire",
          })
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      faqs={serviceFaqs["prewire"]}
      topics={["prewire", "av-installation", "networking", "home-theater", "smart-home", "commercial", "whole-home-video"]}
      eyebrow="New Construction"
      title="Prewire it once. Do it right."
      image={img800.url}
      imageAlt="New construction low-voltage prewire and structured cabling in a Cherry Creek Denver custom home"

      intro={<>The single best time to plan a smart home is before drywall goes up. We partner with builders, architects, and homeowners for <strong>new construction prewire in Denver</strong>, <strong>low-voltage prewire</strong>, and structured cabling that future-proofs the home.</>}
      bullets={[
        "On-site walkthroughs with your builder, architect, and electrician",
        "Structured cabling: Cat6/6A, fiber, speaker wire, coax, control runs",
        "Home theater, media room, and whole-home audio rough-in",
        "Camera, access control, and security device pre-runs",
        "Lighting control, motorized shade, and HVAC sensor wiring",
        "Conduit pulls and back boxes for future-proof flexibility",
        "Detailed labeling, documentation, and as-built drawings",
      ]}
      features={[
        {
          title: "Builder-friendly",
          desc: "Clean trade coordination with framers, electricians, and HVAC — on schedule and on spec.",
        },
        {
          title: "Future-proof by design",
          desc: "Extra conduit and home-runs so tomorrow's tech installs without opening walls.",
        },
        {
          title: "Documented end-to-end",
          desc: "Every cable labeled and drawn — your home's nervous system, fully mapped.",
        },
      ]}
    />
  ),
});
